import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { VerifiedBadge, VerifiedBadgeFull, PendingBadge } from '../components/VerifiedBadge';
import { ImageUpload } from '../components/ImageUpload';
import { toast } from 'sonner';
import { 
  Store, Package, Plus, Edit, Trash2, DollarSign, 
  ShoppingBag, TrendingUp, Save, X, BadgeCheck, Sparkles,
  Truck, CheckCircle, Clock, XCircle, ExternalLink
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ['crochet', 'stickers', 'art', 'keyrings'];

const statusConfig = {
  pending: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Pending' },
  confirmed: { color: 'bg-blue-100 text-blue-700', icon: CheckCircle, label: 'Confirmed' },
  processing: { color: 'bg-purple-100 text-purple-700', icon: Package, label: 'Processing' },
  shipped: { color: 'bg-orange-100 text-orange-700', icon: Truck, label: 'Shipped' },
  delivered: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Delivered' },
  cancelled: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Cancelled' }
};

export default function DashboardPage() {
  const { user, token } = useAuth();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Product form state
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    category: 'crochet',
    stock: '',
    images: [''],
    tags: ''
  });
  const [saving, setSaving] = useState(false);

  // Store edit state
  const [storeForm, setStoreForm] = useState({
    name: '',
    description: '',
    banner_image: '',
    logo: ''
  });
  const [savingStore, setSavingStore] = useState(false);

  // Verification state
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  const [verificationReason, setVerificationReason] = useState('');
  const [requestingVerification, setRequestingVerification] = useState(false);

  // Order management state
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [statusNote, setStatusNote] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await fetch(`${API}/my-store`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStore(data.store);
        setProducts(data.products || []);
        setOrders(data.orders || []);
        setStoreForm({
          name: data.store?.name || '',
          description: data.store?.description || '',
          banner_image: data.store?.banner_image || '',
          logo: data.store?.logo || ''
        });
      }
      
      // Also fetch seller orders
      const ordersRes = await fetch(`${API}/seller/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (ordersRes.ok) {
        const ordersData = await ordersRes.json();
        setOrders(ordersData);
      }
    } catch (e) {
      console.error('Error fetching store:', e);
    } finally {
      setLoading(false);
    }
  };

  const openOrderDialog = (order) => {
    setSelectedOrder(order);
    setTrackingNumber(order.tracking_number || '');
    setTrackingUrl(order.tracking_url || '');
    setStatusNote('');
    setOrderDialogOpen(true);
  };

  const handleUpdateOrderStatus = async (newStatus) => {
    if (!selectedOrder) return;
    
    setUpdatingStatus(true);
    try {
      const body = {
        status: newStatus,
        note: statusNote || undefined
      };
      
      if (newStatus === 'shipped' && trackingNumber) {
        body.tracking_number = trackingNumber;
        body.tracking_url = trackingUrl || undefined;
      }
      
      const res = await fetch(`${API}/seller/orders/${selectedOrder.id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      
      if (res.ok) {
        toast.success(`Order status updated to ${newStatus}`);
        setOrderDialogOpen(false);
        fetchData(); // Refresh orders
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update status');
      }
    } catch (e) {
      toast.error('Error updating order status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleSaveStore = async (e) => {
    e.preventDefault();
    setSavingStore(true);
    try {
      const res = await fetch(`${API}/stores/${store.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(storeForm)
      });
      if (res.ok) {
        const updated = await res.json();
        setStore(updated);
        toast.success('Store updated!');
      } else {
        toast.error('Failed to update store');
      }
    } catch (e) {
      toast.error('Error saving store');
    } finally {
      setSavingStore(false);
    }
  };

  const handleRequestVerification = async (e) => {
    e.preventDefault();
    if (!verificationReason.trim()) {
      toast.error('Please tell us about yourself');
      return;
    }
    setRequestingVerification(true);
    try {
      const res = await fetch(`${API}/auth/request-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ reason: verificationReason })
      });
      if (res.ok) {
        toast.success('Verification request submitted!');
        setVerificationDialogOpen(false);
        setVerificationReason('');
        // Refresh data
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to submit request');
      }
    } catch (e) {
      toast.error('Error submitting request');
    } finally {
      setRequestingVerification(false);
    }
  };

  const openProductDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        name: product.name,
        description: product.description,
        price: product.price.toString(),
        category: product.category,
        stock: product.stock.toString(),
        images: product.images.length > 0 ? product.images : [''],
        tags: product.tags?.join(', ') || ''
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        name: '',
        description: '',
        price: '',
        category: 'crochet',
        stock: '',
        images: [''],
        tags: ''
      });
    }
    setProductDialogOpen(true);
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      name: productForm.name,
      description: productForm.description,
      price: parseFloat(productForm.price),
      category: productForm.category,
      stock: parseInt(productForm.stock) || 0,
      images: productForm.images.filter(img => img.trim()),
      tags: productForm.tags.split(',').map(t => t.trim()).filter(Boolean)
    };

    try {
      const url = editingProduct 
        ? `${API}/products/${editingProduct.id}`
        : `${API}/products`;
      
      const res = await fetch(url, {
        method: editingProduct ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success(editingProduct ? 'Product updated!' : 'Product created!');
        setProductDialogOpen(false);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save product');
      }
    } catch (e) {
      toast.error('Error saving product');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      const res = await fetch(`${API}/products/${productId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Product deleted');
        setProducts(products.filter(p => p.id !== productId));
      } else {
        toast.error('Failed to delete product');
      }
    } catch (e) {
      toast.error('Error deleting product');
    }
  };

  const addImageField = () => {
    setProductForm({ ...productForm, images: [...productForm.images, ''] });
  };

  const updateImage = (index, value) => {
    const newImages = [...productForm.images];
    newImages[index] = value;
    setProductForm({ ...productForm, images: newImages });
  };

  const removeImage = (index) => {
    const newImages = productForm.images.filter((_, i) => i !== index);
    setProductForm({ ...productForm, images: newImages.length ? newImages : [''] });
  };

  // Calculate stats
  const totalRevenue = orders.reduce((acc, o) => acc + (o.total || 0), 0);
  const totalOrders = orders.length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading dashboard..." />
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold mb-4">No Store Found</h2>
          <p className="text-muted-foreground">You need a creator account to access the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-heading font-extrabold flex items-center gap-3">
            <Store className="w-8 h-8" />
            Creator Dashboard
          </h1>
        </div>

        {/* Verification Banner */}
        {!store.is_verified && (
          <div className="card-brutal p-6 mb-8 bg-gradient-to-r from-pastel-art/20 to-pastel-keyrings/20">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-pastel-art/30 flex items-center justify-center flex-shrink-0">
                  <BadgeCheck className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg flex items-center gap-2">
                    Get Verified
                    {user?.verification_status === 'pending' && <PendingBadge />}
                  </h3>
                  <p className="text-muted-foreground text-sm mt-1">
                    {user?.verification_status === 'pending' 
                      ? 'Your verification request is being reviewed. We\'ll notify you once approved!'
                      : 'Earn a verified badge to build trust with buyers and show your connection to the neurodivergent community.'}
                  </p>
                </div>
              </div>
              {user?.verification_status !== 'pending' && (
                <Dialog open={verificationDialogOpen} onOpenChange={setVerificationDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="btn-brutal-primary whitespace-nowrap" data-testid="request-verification-btn">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Request Verification
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <BadgeCheck className="w-5 h-5 text-pastel-art" />
                        Request Verification
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleRequestVerification} className="space-y-4 pt-4">
                      <p className="text-sm text-muted-foreground">
                        Tell us a bit about yourself and your connection to the neurodivergent community. This helps us verify authentic creators.
                      </p>
                      <div className="space-y-2">
                        <Label htmlFor="verificationReason">About You</Label>
                        <Textarea
                          id="verificationReason"
                          value={verificationReason}
                          onChange={(e) => setVerificationReason(e.target.value)}
                          placeholder="Share your story - your neurodivergent journey, what you create, and why you started selling..."
                          className="min-h-[120px]"
                          required
                          data-testid="verification-reason-input"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Your information is kept confidential and only used for verification purposes.
                      </p>
                      <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" onClick={() => setVerificationDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" disabled={requestingVerification} data-testid="submit-verification-btn">
                          {requestingVerification ? 'Submitting...' : 'Submit Request'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        )}

        {store.is_verified && (
          <div className="card-brutal p-4 mb-8 bg-pastel-stickers/10 border-pastel-stickers/30">
            <div className="flex items-center gap-3">
              <VerifiedBadgeFull />
              <span className="text-sm text-muted-foreground">Your store is verified! This badge is shown to buyers.</span>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card-brutal p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-pastel-stickers/30 flex items-center justify-center">
                <Package className="w-5 h-5" />
              </div>
              <span className="text-muted-foreground">Total Products</span>
            </div>
            <p className="text-3xl font-heading font-extrabold">{products.length}</p>
          </div>
          <div className="card-brutal p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-pastel-art/30 flex items-center justify-center">
                <ShoppingBag className="w-5 h-5" />
              </div>
              <span className="text-muted-foreground">Total Orders</span>
            </div>
            <p className="text-3xl font-heading font-extrabold">{totalOrders}</p>
          </div>
          <div className="card-brutal p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-pastel-keyrings/30 flex items-center justify-center">
                <DollarSign className="w-5 h-5" />
              </div>
              <span className="text-muted-foreground">Total Revenue</span>
            </div>
            <p className="text-3xl font-heading font-extrabold">${totalRevenue.toFixed(2)}</p>
          </div>
        </div>

        <Tabs defaultValue="products" className="space-y-6">
          <TabsList className="bg-muted p-1 rounded-xl">
            <TabsTrigger value="products" className="rounded-lg">Products</TabsTrigger>
            <TabsTrigger value="orders" className="rounded-lg">Orders</TabsTrigger>
            <TabsTrigger value="store" className="rounded-lg">Store Settings</TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-heading font-bold">My Products</h2>
              <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-brutal-primary" onClick={() => openProductDialog()} data-testid="add-product-btn">
                    <Plus className="w-5 h-5 mr-2" />
                    Add Product
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSaveProduct} className="space-y-4 pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Product Name</Label>
                        <Input
                          id="name"
                          value={productForm.name}
                          onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                          required
                          data-testid="product-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="category">Category</Label>
                        <Select 
                          value={productForm.category} 
                          onValueChange={(v) => setProductForm({ ...productForm, category: v })}
                        >
                          <SelectTrigger data-testid="product-category-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CATEGORIES.map(cat => (
                              <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={productForm.description}
                        onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                        className="min-h-[100px]"
                        required
                        data-testid="product-description-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="price">Price ($)</Label>
                        <Input
                          id="price"
                          type="number"
                          step="0.01"
                          min="0"
                          value={productForm.price}
                          onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                          required
                          data-testid="product-price-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="stock">Stock</Label>
                        <Input
                          id="stock"
                          type="number"
                          min="0"
                          value={productForm.stock}
                          onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                          data-testid="product-stock-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label>Product Images</Label>
                      <div className="grid grid-cols-3 gap-3">
                        {productForm.images.map((img, idx) => (
                          <ImageUpload
                            key={idx}
                            value={img}
                            onChange={(url) => updateImage(idx, url)}
                            aspectRatio="square"
                            label=""
                          />
                        ))}
                        {productForm.images.length < 6 && (
                          <button
                            type="button"
                            onClick={addImageField}
                            className="aspect-square rounded-xl border-2 border-dashed border-primary/20 hover:border-primary/40 transition-colors flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-foreground"
                          >
                            <Plus className="w-6 h-6" />
                            <span className="text-xs">Add</span>
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">Upload up to 6 images. Click to upload or change.</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="tags">Tags (comma-separated)</Label>
                      <Input
                        id="tags"
                        placeholder="handmade, cute, gift"
                        value={productForm.tags}
                        onChange={(e) => setProductForm({ ...productForm, tags: e.target.value })}
                        data-testid="product-tags-input"
                      />
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setProductDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={saving} data-testid="save-product-btn">
                        {saving ? 'Saving...' : 'Save Product'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {products.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {products.map((product) => (
                  <div key={product.id} className="card-brutal p-4" data-testid={`dashboard-product-${product.id}`}>
                    <div className="flex gap-4">
                      <div className="w-20 h-20 rounded-xl overflow-hidden bg-muted flex-shrink-0">
                        {product.images?.[0] ? (
                          <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="w-8 h-8 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold truncate">{product.name}</h3>
                        <p className="text-sm text-muted-foreground capitalize">{product.category}</p>
                        <p className="font-bold">${product.price?.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground">{product.stock} in stock</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4 pt-4 border-t border-primary/10">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => openProductDialog(product)}
                        data-testid={`edit-product-${product.id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" /> Edit
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDeleteProduct(product.id)}
                        data-testid={`delete-product-${product.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No products yet. Add your first product to start selling!</p>
              </div>
            )}
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-6">
            <h2 className="text-xl font-heading font-bold">Manage Orders</h2>
            {orders.length > 0 ? (
              <div className="space-y-4">
                {orders.map((order) => {
                  const status = statusConfig[order.status] || statusConfig.pending;
                  const StatusIcon = status.icon;
                  
                  return (
                    <div key={order.id} className="card-brutal p-5" data-testid={`seller-order-${order.id}`}>
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <p className="font-heading font-bold">Order #{order.id.slice(0, 8)}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(order.created_at).toLocaleDateString('en-US', {
                              year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </p>
                          {order.buyer_name && (
                            <p className="text-sm mt-1">
                              <span className="text-muted-foreground">Buyer:</span> {order.buyer_name}
                            </p>
                          )}
                        </div>
                        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${status.color}`}>
                          <StatusIcon className="w-4 h-4" />
                          {status.label}
                        </div>
                      </div>
                      
                      {/* Order Items */}
                      <div className="space-y-2 py-3 border-y border-primary/10">
                        {order.items?.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm">
                            <span>{item.name} × {item.quantity}</span>
                            <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                      
                      {/* Tracking Info */}
                      {order.tracking_number && (
                        <div className="mt-3 p-3 rounded-lg bg-pastel-stickers/20">
                          <p className="text-sm font-medium flex items-center gap-2">
                            <Truck className="w-4 h-4" /> Tracking: {order.tracking_number}
                          </p>
                          {order.tracking_url && (
                            <a 
                              href={order.tracking_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm text-pastel-art hover:underline flex items-center gap-1 mt-1"
                            >
                              Track Package <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      )}
                      
                      {/* Footer */}
                      <div className="flex items-center justify-between mt-4">
                        <p className="font-heading font-bold text-lg">
                          Total: ${order.total?.toFixed(2)}
                        </p>
                        
                        {order.status !== 'delivered' && order.status !== 'cancelled' && (
                          <Button 
                            variant="outline" 
                            className="btn-brutal"
                            onClick={() => openOrderDialog(order)}
                            data-testid={`update-order-${order.id}`}
                          >
                            Update Status
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <ShoppingBag className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No orders yet. They'll appear here when customers purchase your products.</p>
              </div>
            )}
            
            {/* Order Status Update Dialog */}
            <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Update Order Status</DialogTitle>
                </DialogHeader>
                {selectedOrder && (
                  <div className="space-y-4 pt-4">
                    <div className="p-3 rounded-lg bg-muted">
                      <p className="font-medium">Order #{selectedOrder.id.slice(0, 8)}</p>
                      <p className="text-sm text-muted-foreground">{selectedOrder.buyer_name}</p>
                      <p className="font-bold mt-1">${selectedOrder.total?.toFixed(2)}</p>
                    </div>
                    
                    {/* Status Buttons */}
                    <div className="space-y-2">
                      <Label>Update Status To:</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {selectedOrder.status === 'confirmed' && (
                          <Button 
                            className="btn-brutal bg-purple-100 text-purple-700 hover:bg-purple-200"
                            onClick={() => handleUpdateOrderStatus('processing')}
                            disabled={updatingStatus}
                          >
                            <Package className="w-4 h-4 mr-2" /> Processing
                          </Button>
                        )}
                        {(selectedOrder.status === 'confirmed' || selectedOrder.status === 'processing') && (
                          <Button 
                            className="btn-brutal bg-orange-100 text-orange-700 hover:bg-orange-200"
                            onClick={() => handleUpdateOrderStatus('shipped')}
                            disabled={updatingStatus}
                          >
                            <Truck className="w-4 h-4 mr-2" /> Shipped
                          </Button>
                        )}
                        {selectedOrder.status === 'shipped' && (
                          <Button 
                            className="btn-brutal bg-green-100 text-green-700 hover:bg-green-200"
                            onClick={() => handleUpdateOrderStatus('delivered')}
                            disabled={updatingStatus}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" /> Delivered
                          </Button>
                        )}
                        {selectedOrder.status !== 'cancelled' && selectedOrder.status !== 'delivered' && (
                          <Button 
                            variant="outline"
                            className="btn-brutal text-red-600 hover:bg-red-50"
                            onClick={() => handleUpdateOrderStatus('cancelled')}
                            disabled={updatingStatus}
                          >
                            <XCircle className="w-4 h-4 mr-2" /> Cancel
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Tracking Info (for shipping) */}
                    {(selectedOrder.status === 'confirmed' || selectedOrder.status === 'processing') && (
                      <div className="space-y-3 p-4 rounded-lg bg-pastel-stickers/10 border border-pastel-stickers/20">
                        <p className="text-sm font-medium flex items-center gap-2">
                          <Truck className="w-4 h-4" /> Shipping Details (optional)
                        </p>
                        <div className="space-y-2">
                          <Input
                            placeholder="Tracking Number"
                            value={trackingNumber}
                            onChange={(e) => setTrackingNumber(e.target.value)}
                            data-testid="tracking-number-input"
                          />
                          <Input
                            placeholder="Tracking URL (optional)"
                            value={trackingUrl}
                            onChange={(e) => setTrackingUrl(e.target.value)}
                            data-testid="tracking-url-input"
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Note */}
                    <div className="space-y-2">
                      <Label>Note (optional)</Label>
                      <Textarea
                        placeholder="Add a note about this status update..."
                        value={statusNote}
                        onChange={(e) => setStatusNote(e.target.value)}
                        className="min-h-[80px]"
                      />
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Store Settings Tab */}
          <TabsContent value="store" className="space-y-6">
            <h2 className="text-xl font-heading font-bold">Store Settings</h2>
            <form onSubmit={handleSaveStore} className="space-y-6">
              <div className="card-brutal p-6 space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="storeName">Store Name</Label>
                  <Input
                    id="storeName"
                    value={storeForm.name}
                    onChange={(e) => setStoreForm({ ...storeForm, name: e.target.value })}
                    required
                    data-testid="store-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="storeDescription">Description</Label>
                  <Textarea
                    id="storeDescription"
                    value={storeForm.description}
                    onChange={(e) => setStoreForm({ ...storeForm, description: e.target.value })}
                    className="min-h-[100px]"
                    required
                    data-testid="store-description-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ImageUpload
                    label="Store Logo"
                    value={storeForm.logo}
                    onChange={(url) => setStoreForm({ ...storeForm, logo: url })}
                    aspectRatio="logo"
                  />
                  
                  <ImageUpload
                    label="Banner Image"
                    value={storeForm.banner_image}
                    onChange={(url) => setStoreForm({ ...storeForm, banner_image: url })}
                    aspectRatio="banner"
                  />
                </div>
              </div>

              <Button type="submit" className="btn-brutal-primary" disabled={savingStore} data-testid="save-store-btn">
                <Save className="w-5 h-5 mr-2" />
                {savingStore ? 'Saving...' : 'Save Store Settings'}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
